import React, { useEffect, useMemo, useRef } from 'react';

function resolveImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('blob:') || image.startsWith('/')) return image;
  const apiBase = import.meta.env.VITE_API_URL || '';
  return `${apiBase}/uploads/gallery/${image}`;
}

function getTileSrcRect(tileIndex, columnsCount, tileSize) {
  const cols = Math.max(1, Number(columnsCount) || 1);
  const index = Math.max(0, Number(tileIndex) || 0);
  return {
    sx: (index % cols) * tileSize,
    sy: Math.floor(index / cols) * tileSize
  };
}

function getActorFrameRect(frameIndex, directionRow, frameWidth, frameHeight, columnsCount) {
  const columns = Math.max(1, Number(columnsCount) || 1);
  const index = Math.max(0, Number(frameIndex) || 0);
  const row = Math.max(0, Number(directionRow) || 0);
  if (index >= columns) {
    return {
      sx: (index % columns) * frameWidth,
      sy: Math.floor(index / columns) * frameHeight
    };
  }
  return {
    sx: index * frameWidth,
    sy: row * frameHeight
  };
}

function normalizeAppearance(appearance = {}, fallbackImage = null) {
  const metadata = appearance?.metadata && typeof appearance.metadata === 'object' ? appearance.metadata : {};
  return {
    sprite_image: appearance?.sprite_image || fallbackImage || null,
    frame_width: Math.max(8, Number(appearance?.frame_width) || 32),
    frame_height: Math.max(8, Number(appearance?.frame_height) || 48),
    columns_count: Math.max(1, Number(appearance?.columns_count) || 3),
    rows_count: Math.max(1, Number(appearance?.rows_count) || 4),
    direction_map: appearance?.direction_map && typeof appearance.direction_map === 'object' ? appearance.direction_map : (metadata.direction_map || { south: 0, west: 1, east: 2, north: 3 }),
    animation_map: appearance?.animation_map && typeof appearance.animation_map === 'object' ? appearance.animation_map : (metadata.animation_map || { idle: [1], walk: [0, 1, 2, 1] }),
    scale: Math.max(0.5, Number(appearance?.scale ?? metadata.scale) || 1.65),
    show_foot_shadow: appearance?.show_foot_shadow !== false && metadata.show_foot_shadow !== false,
    metadata
  };
}

function getAnimationFrames(animationMap, behavior) {
  if (Array.isArray(animationMap?.[behavior]) && animationMap[behavior].length) {
    return animationMap[behavior];
  }
  if (behavior !== 'idle' && Array.isArray(animationMap?.idle) && animationMap.idle.length) {
    return animationMap.idle;
  }
  return [1];
}

function drawActor(ctx, {
  image,
  appearance,
  facing,
  behavior,
  tick,
  tileX,
  tileY,
  tileSize,
  fallbackColor = '#7c3aed'
}) {
  const normalized = normalizeAppearance(appearance);
  const frames = getAnimationFrames(normalized.animation_map, behavior);
  const frameIndex = Number(frames[tick % frames.length] ?? frames[0] ?? 1) || 0;
  const directionRow = Number(normalized.direction_map?.[facing] ?? normalized.direction_map?.south ?? 0) || 0;
  const { sx, sy } = getActorFrameRect(
    frameIndex,
    directionRow,
    normalized.frame_width,
    normalized.frame_height,
    normalized.columns_count
  );
  const destHeight = Math.max(tileSize, Math.round(tileSize * normalized.scale));
  const destWidth = Math.max(Math.round(destHeight * (normalized.frame_width / normalized.frame_height)), Math.round(tileSize * 0.72));
  const dx = tileX + Math.round((tileSize - destWidth) / 2);
  const dy = tileY + tileSize - destHeight;

  if (image && image.complete && image.naturalWidth > 0) {
    try {
      ctx.drawImage(
        image,
        sx,
        sy,
        normalized.frame_width,
        normalized.frame_height,
        dx,
        dy,
        destWidth,
        destHeight
      );
      return;
    } catch (_) {
      // Fallback below.
    }
  }

  ctx.save();
  ctx.fillStyle = fallbackColor;
  ctx.beginPath();
  ctx.arc(tileX + tileSize / 2, tileY + tileSize / 2, Math.max(6, tileSize * 0.28), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawCrop(ctx, {
  image,
  crop,
  tileX,
  tileY,
  tileSize
}) {
  const stageCount = Math.max(2, Number(crop?.stage_count || 7));
  const stage = Math.max(1, Number(crop?.stage || 1));
  const progress = Math.max(0, Math.min(1, Number(crop?.progress ?? ((stage - 1) / Math.max(1, stageCount - 1))) || 0));
  const ready = Boolean(crop?.is_ready);
  const widthScale = ready ? 0.82 : 0.42 + (progress * 0.32);
  const heightScale = ready ? 0.88 : 0.45 + (progress * 0.38);
  const baseWidth = Math.max(14, Math.round(tileSize * widthScale));
  const baseHeight = Math.max(18, Math.round(tileSize * heightScale));
  const dx = tileX + Math.round((tileSize - baseWidth) / 2);
  const dy = tileY + tileSize - baseHeight - Math.max(2, Math.round(tileSize * 0.04));

  if (image && image.complete && image.naturalWidth > 0) {
    const aspect = image.naturalWidth / Math.max(1, image.naturalHeight);
    let drawWidth = baseWidth;
    let drawHeight = baseHeight;
    if (aspect >= 1) {
      drawWidth = Math.min(baseWidth, Math.round(baseHeight * aspect));
      drawHeight = Math.max(12, Math.round(drawWidth / aspect));
    } else {
      drawHeight = Math.min(baseHeight, Math.round(baseWidth / aspect));
      drawWidth = Math.max(12, Math.round(drawHeight * aspect));
    }
    const drawX = tileX + Math.round((tileSize - drawWidth) / 2);
    const drawY = tileY + tileSize - drawHeight - Math.max(2, Math.round(tileSize * 0.04));
    try {
      ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
      return;
    } catch (_) {
      // Fallback below.
    }
  }

  ctx.save();
  ctx.translate(tileX + tileSize / 2, tileY + tileSize / 2);
  const stemHeight = Math.max(6, Math.round(tileSize * (ready ? 0.36 : 0.26 + progress * 0.16)));
  const leafSize = Math.max(4, Math.round(tileSize * (ready ? 0.18 : 0.12 + progress * 0.08)));
  ctx.strokeStyle = ready ? '#86efac' : '#4ade80';
  ctx.fillStyle = ready ? '#22c55e' : '#3b82f6';
  ctx.lineWidth = Math.max(2, Math.round(tileSize * 0.05));
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, tileSize * 0.18);
  ctx.lineTo(0, tileSize * 0.18 - stemHeight);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(-leafSize * 0.8, tileSize * 0.04, leafSize, leafSize * 0.55, -0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(leafSize * 0.8, tileSize * 0.04, leafSize, leafSize * 0.55, 0.45, 0, Math.PI * 2);
  ctx.fill();
  if (ready) {
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.arc(0, tileSize * -0.05, Math.max(3, Math.round(tileSize * 0.08)), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function normalizeCropVisual(crop, now = Date.now()) {
  const growthSeconds = Math.max(1, Number(crop?.growth_seconds || 420));
  const stageCount = Math.max(2, Number(crop?.stage_count || 7));
  const plantedAt = crop?.planted_at ? new Date(crop.planted_at).getTime() : 0;
  const readyAt = crop?.ready_at ? new Date(crop.ready_at).getTime() : 0;
  const totalMs = growthSeconds * 1000;
  const remainingSeconds = readyAt > 0
    ? Math.max(0, Math.ceil((readyAt - now) / 1000))
    : Math.max(0, Number(crop?.remaining_seconds || 0));
  const progress = plantedAt > 0
    ? Math.min(1, Math.max(0, (now - plantedAt) / totalMs))
    : Math.max(0, Number(crop?.progress || 0));
  const stage = plantedAt > 0
    ? Math.min(stageCount, Math.max(1, Math.floor(progress * stageCount) + 1))
    : Math.max(1, Number(crop?.stage || 1));

  return {
    ...crop,
    stage,
    stage_count: stageCount,
    progress,
    remaining_seconds: remainingSeconds,
    is_ready: remainingSeconds <= 0
  };
}

const FALLBACK_ROLE_COLORS = {
  terrain: ['#32513c', '#44654c', '#56775d'],
  props: ['#5a7d60', '#728f78', '#8ba08f'],
  buildings: ['#6c5a4c', '#806955', '#947a63'],
  objects: ['#6a705c', '#7e856d', '#909779'],
  resources: ['#6b7d56', '#7d8e67', '#8ea078']
};

function drawDirectionMarker(ctx, x, y, size, relation, color) {
  const centerX = x + size / 2;
  const centerY = y + size / 2;
  const arrowSize = Math.max(5, Math.round(size * 0.18));
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(255,255,255,0.75)';
  ctx.lineWidth = 1.5;

  ctx.beginPath();
  if (relation === 'north') {
    ctx.moveTo(centerX, y + 3);
    ctx.lineTo(centerX - arrowSize, y + 3 + arrowSize);
    ctx.lineTo(centerX + arrowSize, y + 3 + arrowSize);
  } else if (relation === 'south') {
    ctx.moveTo(centerX, y + size - 3);
    ctx.lineTo(centerX - arrowSize, y + size - 3 - arrowSize);
    ctx.lineTo(centerX + arrowSize, y + size - 3 - arrowSize);
  } else if (relation === 'east') {
    ctx.moveTo(x + size - 3, centerY);
    ctx.lineTo(x + size - 3 - arrowSize, centerY - arrowSize);
    ctx.lineTo(x + size - 3 - arrowSize, centerY + arrowSize);
  } else if (relation === 'west') {
    ctx.moveTo(x + 3, centerY);
    ctx.lineTo(x + 3 + arrowSize, centerY - arrowSize);
    ctx.lineTo(x + 3 + arrowSize, centerY + arrowSize);
  } else {
    ctx.arc(centerX, centerY, Math.max(3, Math.round(size * 0.12)), 0, Math.PI * 2);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

export default function RpgCityCanvas({
  scene,
  player,
  onReady,
  tileSize = 24,
  viewport = null
}) {
  const canvasRef = useRef(null);
  const imagesRef = useRef({});
  const [imagesVersion, bumpImagesVersion] = React.useState(0);
  const [tick, setTick] = React.useState(0);

  const tilesetMaps = useMemo(() => {
    const byKey = new Map();
    const byId = new Map();
    const byRole = new Map();
    (scene?.tilesets || []).forEach((tileset) => {
      const tilesetId = Number(tileset.tileset_id || tileset.id || tileset.tileset?.id || 0);
      const role = String(tileset.role || 'terrain');
      const sortOrder = Number(tileset.sort_order) || 0;
      const assignmentKey = String(tileset.assignment_key || `${tilesetId}:${role}:${sortOrder}`);
      const normalized = {
        ...tileset,
        tileset_id: tilesetId,
        role,
        sort_order: sortOrder,
        assignment_key: assignmentKey
      };
      if (assignmentKey) byKey.set(assignmentKey, normalized);
      if (tilesetId) byId.set(tilesetId, normalized);
      if (!byRole.has(role)) byRole.set(role, normalized);
    });
    return { byKey, byId, byRole };
  }, [scene?.tilesets]);

  const worldWidth = Number(scene?.width) || 1;
  const worldHeight = Number(scene?.height) || 1;
  const viewportWidth = Math.max(1, Math.min(worldWidth, Number(viewport?.width) || worldWidth));
  const viewportHeight = Math.max(1, Math.min(worldHeight, Number(viewport?.height) || worldHeight));
  const viewportMode = Boolean(viewport);
  const fallbackFocusX = Number(player?.x ?? 0);
  const fallbackFocusY = Number(player?.y ?? 0);
  const focusX = Math.min(
    Math.max(0, Number.isFinite(Number(viewport?.centerX)) ? Number(viewport.centerX) : fallbackFocusX),
    Math.max(0, worldWidth - 1)
  );
  const focusY = Math.min(
    Math.max(0, Number.isFinite(Number(viewport?.centerY)) ? Number(viewport.centerY) : fallbackFocusY),
    Math.max(0, worldHeight - 1)
  );
  const maxStartX = Math.max(0, worldWidth - viewportWidth);
  const maxStartY = Math.max(0, worldHeight - viewportHeight);
  const viewOriginX = viewportMode ? Math.min(maxStartX, Math.max(0, focusX - Math.floor(viewportWidth / 2))) : 0;
  const viewOriginY = viewportMode ? Math.min(maxStartY, Math.max(0, focusY - Math.floor(viewportHeight / 2))) : 0;
  const startX = Math.max(0, Math.floor(viewOriginX));
  const startY = Math.max(0, Math.floor(viewOriginY));
  const endX = viewportMode ? Math.min(worldWidth, Math.ceil(viewOriginX + viewportWidth) + 1) : worldWidth;
  const endY = viewportMode ? Math.min(worldHeight, Math.ceil(viewOriginY + viewportHeight) + 1) : worldHeight;
  const renderWidth = viewportMode ? viewportWidth : worldWidth;
  const renderHeight = viewportMode ? viewportHeight : worldHeight;

  const npcImageEntries = useMemo(() => {
    const map = new Map();
    (scene?.npcs || []).forEach((npc) => {
      const template = npc?.template || {};
      const key = `npc:${Number(template.id || npc.template_id || npc.id || 0)}`;
      if (map.has(key)) return;
      map.set(key, {
        key,
        image: template.sprite_image || npc.sprite_image || null
      });
    });
    const playerImage = player?.appearance?.sprite_image || null;
    if (playerImage) {
      map.set('player', {
        key: 'player',
        image: playerImage
      });
    }
    return Array.from(map.values());
  }, [player?.appearance?.sprite_image, scene?.npcs]);

  const cropImageEntries = useMemo(() => {
    const map = new Map();
    (scene?.crops || []).forEach((crop) => {
      const key = `crop:${Number(crop.id || crop.coordinate || 0)}:${Number(crop.layer || 0)}`;
      if (map.has(key)) return;
      const image = crop?.is_ready
        ? (crop.crop_image || crop.seed_image || null)
        : (crop.seed_image || crop.crop_image || null);
      map.set(key, {
        key,
        image
      });
    });
    return Array.from(map.values());
  }, [scene?.crops]);

  useEffect(() => {
    const entries = Array.from(tilesetMaps.byKey.values());
    let cancelled = false;
    const loaders = entries.map((tileset) => {
      const tilesetKey = String(tileset.assignment_key || tileset.tileset_id || tileset.role || '');
      if (!tileset?.image) {
        imagesRef.current[tilesetKey] = null;
        bumpImagesVersion((value) => value + 1);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (!cancelled) {
            imagesRef.current[tilesetKey] = img;
            bumpImagesVersion((value) => value + 1);
            resolve();
          }
        };
        img.onerror = () => {
          if (!cancelled) {
            imagesRef.current[tilesetKey] = null;
            bumpImagesVersion((value) => value + 1);
            resolve();
          }
        };
        img.src = resolveImageSrc(tileset.image);
      });
    });

    Promise.all(loaders).then(() => {
      if (!cancelled) onReady?.();
    });

    return () => {
      cancelled = true;
    };
  }, [onReady, tilesetMaps]);

  useEffect(() => {
    if (!npcImageEntries.length) return undefined;
    let cancelled = false;
    const loaders = npcImageEntries.map((entry) => {
      if (!entry.image) {
        imagesRef.current[entry.key] = null;
        bumpImagesVersion((value) => value + 1);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (!cancelled) {
            imagesRef.current[entry.key] = img;
            bumpImagesVersion((value) => value + 1);
            resolve();
          }
        };
        img.onerror = () => {
          if (!cancelled) {
            imagesRef.current[entry.key] = null;
            bumpImagesVersion((value) => value + 1);
            resolve();
          }
        };
        img.src = resolveImageSrc(entry.image);
      });
    });

    Promise.all(loaders).then(() => {
      if (!cancelled) onReady?.();
    });

    return () => {
      cancelled = true;
    };
  }, [npcImageEntries, onReady]);

  useEffect(() => {
    if (!cropImageEntries.length) return undefined;
    let cancelled = false;
    const loaders = cropImageEntries.map((entry) => {
      if (!entry.image) {
        imagesRef.current[entry.key] = null;
        bumpImagesVersion((value) => value + 1);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (!cancelled) {
            imagesRef.current[entry.key] = img;
            bumpImagesVersion((value) => value + 1);
            resolve();
          }
        };
        img.onerror = () => {
          if (!cancelled) {
            imagesRef.current[entry.key] = null;
            bumpImagesVersion((value) => value + 1);
            resolve();
          }
        };
        img.src = resolveImageSrc(entry.image);
      });
    });

    Promise.all(loaders).then(() => {
      if (!cancelled) onReady?.();
    });

    return () => {
      cancelled = true;
    };
  }, [cropImageEntries, onReady]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTick((value) => value + 1);
    }, 240);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !scene) return;

    const width = worldWidth;
    const height = worldHeight;
    canvas.width = renderWidth * tileSize;
    canvas.height = renderHeight * tileSize;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const tiles = Array.isArray(scene.tiles) ? scene.tiles : [];
    const tileByKey = new Map();
    tiles.forEach((tile) => {
      const key = `${Number(tile.layer) || 0}:${Number(tile.coordinate) || 0}`;
      tileByKey.set(key, tile);
    });

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#050711';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (let layer = 0; layer <= 2; layer += 1) {
      for (let y = startY; y < endY; y += 1) {
        for (let x = startX; x < endX; x += 1) {
          const coordinate = y * width + x;
          const tile = tileByKey.get(`${layer}:${coordinate}`);
          if (!tile) continue;

          const metadata = tile.metadata && typeof tile.metadata === 'object' ? tile.metadata : {};
          const tileTilesetId = Number(metadata.tileset_id) || Number(tile.tileset_id) || 0;
          const tileAssignmentKey = String(metadata.tileset_assignment_key || tile.tileset_assignment_key || '').trim();
          const tileRole = String(metadata.tileset_role || tile.tileset_role || tile.tilesetKey || 'terrain').trim() || 'terrain';
          const tileset = (tileAssignmentKey && tilesetMaps.byKey.get(tileAssignmentKey))
            || (tileTilesetId && tilesetMaps.byId.get(tileTilesetId))
            || tilesetMaps.byRole.get(tileRole)
            || tilesetMaps.byRole.get('terrain')
            || null;
          const imageKey = String(tileset?.assignment_key || tileset?.tileset_id || tileset?.role || '');
          const image = imageKey ? imagesRef.current[imageKey] : null;
          const sourceSize = Number(tileset?.tile_size) || 48;
          const columns = Math.max(
            1,
            Number(tileset?.columns_count)
            || Math.floor(Number(image?.naturalWidth || 0) / sourceSize)
            || 1
          );
          const { sx, sy } = getTileSrcRect(tile.tile_index, columns, sourceSize);
          const dx = (x - viewOriginX) * tileSize;
          const dy = (y - viewOriginY) * tileSize;

          if (image && image.complete && image.naturalWidth > 0) {
            try {
              ctx.drawImage(image, sx, sy, sourceSize, sourceSize, dx, dy, tileSize, tileSize);
            } catch (_) {
              const colors = FALLBACK_ROLE_COLORS[tileRole] || FALLBACK_ROLE_COLORS.terrain;
              ctx.fillStyle = colors[(Number(tile.tile_index) || 0) % colors.length];
              ctx.fillRect(dx, dy, tileSize, tileSize);
            }
          } else {
            const colors = FALLBACK_ROLE_COLORS[tileRole] || FALLBACK_ROLE_COLORS.terrain;
            ctx.fillStyle = colors[(Number(tile.tile_index) || 0) % colors.length];
            ctx.fillRect(dx, dy, tileSize, tileSize);
          }

          if (Number(tile.blocked) === 1) {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.14)';
            ctx.fillRect(dx, dy, tileSize, tileSize);
          }
        }
      }
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= (viewportMode ? renderWidth : width); x += 1) {
      ctx.beginPath();
      ctx.moveTo(x * tileSize, 0);
      ctx.lineTo(x * tileSize, (viewportMode ? renderHeight : height) * tileSize);
      ctx.stroke();
    }
    for (let y = 0; y <= (viewportMode ? renderHeight : height); y += 1) {
      ctx.beginPath();
      ctx.moveTo(0, y * tileSize);
      ctx.lineTo((viewportMode ? renderWidth : width) * tileSize, y * tileSize);
      ctx.stroke();
    }

    const now = Date.now();
    for (const crop of Array.isArray(scene.crops) ? scene.crops : []) {
      if (!crop) continue;
      const cropX = Number(crop.coordinate || 0) % width;
      const cropY = Math.floor(Number(crop.coordinate || 0) / width);
      if (viewportMode && (cropX < startX || cropX >= endX || cropY < startY || cropY >= endY)) continue;
      const cropKey = `crop:${Number(crop.id || crop.coordinate || 0)}:${Number(crop.layer || 0)}`;
      const image = imagesRef.current[cropKey] || null;
      drawCrop(ctx, {
        image,
        crop: normalizeCropVisual(crop, now),
        tileX: (cropX - viewOriginX) * tileSize,
        tileY: (cropY - viewOriginY) * tileSize,
        tileSize
      });
    }

    for (const npc of Array.isArray(scene.npcs) ? scene.npcs : []) {
      if (!npc || Number(npc.is_enabled) === 0) continue;
      const npcId = Number(npc.id || 0);
      const template = npc.template || {};
      const npcKey = `npc:${Number(template.id || npc.template_id || npcId || 0)}`;
      const image = imagesRef.current[npcKey] || null;
      const npcX = Number(npc.x || 0);
      const npcY = Number(npc.y || 0);
      const npcAppearance = template?.appearance || template || {};
      const usePlayerScale = npcAppearance?.metadata?.use_player_scale !== false;
      const resolvedNpcScale = usePlayerScale
        ? Math.max(0.5, Number(player?.appearance?.scale) || Number(npcAppearance?.scale) || 1.65)
        : Math.max(0.5, Number(npcAppearance?.scale ?? npcAppearance?.metadata?.scale) || 1.65);
      if (viewportMode && (npcX < startX || npcX >= endX || npcY < startY || npcY >= endY)) continue;
      drawActor(ctx, {
        image,
        appearance: {
          ...npcAppearance,
          scale: resolvedNpcScale
        },
        facing: String(npc.facing || 'south').trim() || 'south',
        behavior: String(npc.behavior || 'idle').trim() || 'idle',
        tick,
        tileX: (npcX - viewOriginX) * tileSize,
        tileY: (npcY - viewOriginY) * tileSize,
        tileSize,
        fallbackColor: '#7c3aed'
      });
    }

    if (player) {
      const px = Number(player.x) || 0;
      const py = Number(player.y) || 0;
      const tileX = (px - viewOriginX) * tileSize;
      const tileY = (py - viewOriginY) * tileSize;

      if (player.appearance?.show_foot_shadow !== false) {
        ctx.save();
        ctx.fillStyle = 'rgba(34, 211, 238, 0.22)';
        ctx.beginPath();
        ctx.ellipse(tileX + tileSize / 2, tileY + tileSize - 5, Math.max(8, tileSize * 0.28), Math.max(4, tileSize * 0.12), 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (player.appearance?.sprite_image) {
        drawActor(ctx, {
          image: imagesRef.current.player || null,
          appearance: player.appearance,
          facing: String(player.facing || player.direction || 'south').trim() || 'south',
          behavior: ['moving', 'saving'].includes(String(player.movement_state || '').trim()) ? 'walk' : 'idle',
          tick,
          tileX,
          tileY,
          tileSize,
          fallbackColor: '#67e8f9'
        });
      } else {
        const cx = tileX + tileSize / 2;
        const cy = tileY + tileSize / 2;
        ctx.save();
        ctx.shadowColor = 'rgba(34, 211, 238, 0.85)';
        ctx.shadowBlur = 16;
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(6, Math.round(tileSize * 0.28)), 0, Math.PI * 2);
        ctx.fillStyle = '#67e8f9';
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#f8fafc';
        ctx.stroke();
        ctx.restore();
      }
    }
  }, [endX, endY, imagesVersion, player, scene, startX, startY, tick, tileSize, tilesetMaps, viewOriginX, viewOriginY, viewportMode, worldWidth]);

  return (
    <canvas
      ref={canvasRef}
      className="rounded-3xl border border-cyan-400/20 bg-[#060816] shadow-[0_0_60px_rgba(17,24,39,0.5)] max-w-full h-auto"
    />
  );
}
